import cloneDeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import { Div, notification, SubTitle } from 'ui';
import { withShadow } from 'ui/styles';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { orderStatus } from 'utils/app-configs';
import { withRouter } from 'utils/withRouter';
import { addSupersededPart, getLogisticsDatesRequest } from '../containers/parts-actions';
import PartDetails from './part-details';
import PartList from './part-list';
import PartToolbar from './part-toolbar';

const Wrapper = styled(Div)`
  ${withShadow};
  border-radius: 4px;
  background-color: #fff;
  padding: 8px 13px 13px;
  margin-bottom: 20px;
`;

const StyledList = styled.ul`
  overflow: auto;
  max-height: 40vh;
`;

const Parts = ({
  data = {},
  orderId,
  form,
  disabled,
  addedParts,
  searchPartRequest,
  addPartRequest,
  addSuccess,
  history,
  setFormDirty,
  shipTo,
  activeStatus,
  intl,
  onSidepanelOpen,
  onSidepanelClosed,
  decimalMask,
  serviceCenterId,
  successfullyAddedSupersededPart,
  openSupersededParts,
}) => {
  const dispatch = useDispatch();
  const [selectedPart, setSelectedPart] = useState(null);
  const [filter, setFilter] = useState('all');
  const [checkUpdate, setCheckUpdate] = useState(false);
  const { getFieldDecorator, setFieldsValue, getFieldValue } = form;

  const { logisticsDates, resetLogisticsDates } = useSelector(state => state.order.parts);

  useEffect(() => {
    if (getFieldValue('orderPartList.parts')) {
      const existingData = cloneDeep(getFieldValue('orderPartList.parts'));
      if (existingData?.length && orderId && !logisticsDates?.length) {
        dispatch(getLogisticsDatesRequest(orderId));
      }
    }
  }, [getFieldValue('orderPartList.parts')]);

  useEffect(() => {
    if (resetLogisticsDates && orderId) {
      dispatch(getLogisticsDatesRequest(orderId));
    }
  }, [resetLogisticsDates]);

  useDeepCompareEffect(() => {
    if (logisticsDates?.length) {
      const existingParts = cloneDeep(getFieldValue('orderPartList.parts'));
      const fullParts = existingParts?.map((p) => {
        const logisticsDateOrder = logisticsDates?.find(l => l.partId === p.id);
        if (logisticsDateOrder) {
          return {
            ...logisticsDateOrder,
            ...p,
          };
        }
        return p;
      });
      setFieldsValue({
        'orderPartList.parts': fullParts || [],
      });
    }
  }, [logisticsDates]);

  useDeepCompareEffect(() => {
    // If data (including urgency and/or order consolidation) changed,
    // update it (can happen when partners change on order)
    const existingData = cloneDeep(getFieldValue('orderPartList'));
    setFieldsValue({
      orderPartList: {
        ...existingData,
        parts: data?.parts || [],
        urgency: data?.urgency,
        partialShipment: data?.partialShipment,
      },
    });
  }, [data]);

  useEffect(() => {
    if (addSuccess && addedParts?.length) {
      const existingParts = cloneDeep(getFieldValue('orderPartList.parts'));
      setFieldsValue({
        'orderPartList.parts': [...existingParts, ...addedParts],
      });
      setCheckUpdate(true);
      setFormDirty();
      // reset filter to make sure that you'll see what you've added
      setFilter('all');
      successfullyAddedSupersededPart();
    }
  }, [addSuccess]);

  const getFilterOptions = () => [
    {
      text: intl.formatMessage({ id: 'order.parts.ALL' }),
      value: 'all',
    },
    {
      text: intl.formatMessage({ id: 'order.parts.NOT_DELIVERED' }),
      value: 'notDelivered',
    },
    {
      text: intl.formatMessage({ id: 'order.parts.DELIVERED' }),
      value: 'delivered',
    },
  ];

  const handleShowDetails = (part) => {
    if (onSidepanelOpen) {
      onSidepanelOpen();
    }
    setSelectedPart(part);
    history.push('part-details', { cancelBlock: true });
  };

  const handleClose = () => {
    if (onSidepanelClosed) {
      onSidepanelClosed();
    }
    setSelectedPart(null);
  };

  const handleUpdateParts = (partsFromExcel) => {
    if (!disabled && partsFromExcel) {
      const orderPartList = cloneDeep(getFieldValue('orderPartList.parts'));
      let notAddedParts = [];
      partsFromExcel.forEach((k) => {
        const foundPart = orderPartList.findLast(p =>
          !p.itemNumber && k.number === p.materialNumber);
        if (!foundPart && notAddedParts?.length) {
          notAddedParts = [
            ...notAddedParts,
            k,
          ];
        }
      });

      if (notAddedParts?.length) {
        notification.warning({
          message: <div>
            {intl.formatMessage({
              id: 'order.parts.ADD_MATERIAL_FAILED',
            })}
            <StyledList>
              {notAddedParts?.map(p =>
                <li key={p}>{p}</li>,
              )}
            </StyledList>
          </div>,
          duration: 0,
          placement: 'top',
        });
      }
      setFieldsValue({
        'orderPartList.parts': orderPartList,
      });
      setFormDirty();
    }
    setCheckUpdate(false);
  };

  const handleAddPart = (materialsDetails) => {
    addPartRequest({
      orderId,
      materials:
        materialsDetails?.map(({ materialNumber, vendorId, quantity }) =>
          ({ materialNumber, vendorId, quantity })),
    });
  };

  const handleRemovePart = (part) => {
    if (!disabled) {
      const parts = cloneDeep(getFieldValue('orderPartList.parts'));
      const newParts = parts?.filter(o => o.id !== part.id);

      setFieldsValue({
        'orderPartList.parts': newParts,
      });
      setFormDirty();
    }
  };

  const handleFilterChange = (e) => {
    setFilter(e);
  };

  // handle the superseded parts list here, to avoid problem when adding the same obsolete parts
  const handleRemoveSupersededPart = (supersededPartId, partId) => {
    const parts = cloneDeep(getFieldValue('orderPartList.parts'));
    const editedPart = parts?.find(p => p.id === partId);
    if (editedPart) {
      editedPart.supersededBy = editedPart?.supersededBy?.filter(s => s.id !== supersededPartId);
    }
    setFieldsValue({
      'orderPartList.parts': parts,
    });
    setFormDirty();
  };

  const handleAddOfSupersededPart = (supersededPart, originalPart) => {
    supersededPart.autoReplacementInfo.isAutoReplaced = false;
    supersededPart.autoReplacementInfo.originalPartDescription =
      originalPart?.materialDescription || '';
    supersededPart.autoReplacementInfo.originalPartNumber =
      originalPart?.materialNumber || '';

    dispatch(addSupersededPart(supersededPart));
  };

  const filterParts = (parts) => {
    switch (filter) {
      case 'notDelivered':
        return parts?.filter(p => !p.deliveries || !p.deliveries.length);
      case 'delivered':
        return parts?.filter(p => p.deliveries && p.deliveries.length);
      default:
        return parts;
    }
  };

  const inactive = disabled || (activeStatus && activeStatus.id > orderStatus.executed);

  getFieldDecorator('orderPartList.parts', {
    initialValue: filterParts(data?.parts),
  });

  const parts = filterParts(getFieldValue('orderPartList.parts'));

  const filterOptions = getFilterOptions();

  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  return (
    <Div marginTop={45}>
      <SubTitle>
        <FormattedMessage id='common.titles.PARTS_TITLE_COUNT' values={{ count: parts?.length }} />
        <PartToolbar
          filter={filter}
          filterOptions={filterOptions}
          parts={parts}
          handleFilterChange={handleFilterChange}
        />
      </SubTitle>
      {orderId &&
        <Wrapper>
          <PartList
            parts={parts}
            partList={data}
            isFrozen={data?.isFrozen}
            orderId={orderId}
            form={form}
            disabled={inactive}
            onShowDetails={handleShowDetails}
            handleUpdateParts={handleUpdateParts}
            handleRemovePart={handleRemovePart}
            handleAddPart={handleAddPart}
            searchPartRequest={searchPartRequest}
            shipTo={shipTo}
            decimalMask={decimalMask}
            checkUpdate={checkUpdate}
            serviceCenterId={serviceCenterId}
            openSupersededParts={openSupersededParts}
            handleRemoveSupersededPart={handleRemoveSupersededPart}
            handleAddOfSupersededPart={handleAddOfSupersededPart}
          />
        </Wrapper>
      }
      <Routes>
        <Route
          path={'part-details'}
          element={<PartDetails part={selectedPart} onClose={handleClose} />}
        />
      </Routes>
    </Div>
  );
};

Parts.propTypes = {
  data: PropTypes.object,
  orderId: PropTypes.string,
  form: PropTypes.object,
  disabled: PropTypes.bool,
  addedParts: PropTypes.array,
  searchPartRequest: PropTypes.func,
  addPartRequest: PropTypes.func,
  addSuccess: PropTypes.bool,
  history: PropTypes.object,
  setFormDirty: PropTypes.func,
  shipTo: PropTypes.object,
  activeStatus: PropTypes.object,
  intl: PropTypes.object,
  onSidepanelOpen: PropTypes.func,
  onSidepanelClosed: PropTypes.func,
  decimalMask: PropTypes.bool,
  serviceCenterId: PropTypes.string,
  successfullyAddedSupersededPart: PropTypes.func,
  openSupersededParts: PropTypes.func,
};

export default injectIntl(withRouter(Parts));
